import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@material-ui/core'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { canonicalizeRow, describeError, inspectHeaders } from 'src/util/bulkImport'
import { parseCsv } from 'src/util/csv'


const describeFailure = failure =>
  failure.label
    ? `${failure.label}: ${failure.reason}`
    : `Row ${failure.line} (${failure.name}): ${failure.reason}`

const BulkUploadPanel = ({
  entityPlural,
  headers,
  onDownloadSample,
  onImport,
  toggleDrawer,
  refetch,
  helpText,
  allOrNothing = false,
}) => {
  const [csvFile, setCsvFile] = useState(null)
  const [formError, setFormError] = useState('')
  const [ignoredColumns, setIgnoredColumns] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [progress, setProgress] = useState('')
  const [results, setResults] = useState(null)

  const handleFileChange = event => {
    setCsvFile(event.target.files?.[0] || null)
    setResults(null)
    setFormError('')
    setIgnoredColumns([])
    setProgress('')
  }

  const handleSubmit = async () => {
    setFormError('')
    setResults(null)
    setIgnoredColumns([])
    setProgress('')

    if (!csvFile) {
      setFormError('Please choose a CSV file')
      return
    }

    setIsSubmitting(true)
    try {
      const parsedRows = parseCsv(await csvFile.text())
      if (!parsedRows.length) {
        setFormError('The CSV file has no data rows')
        return
      }

      setIgnoredColumns(inspectHeaders(parsedRows[0], headers))
      const rows = parsedRows.map(row => canonicalizeRow(row, headers))

      const outcome = await onImport(rows, { onProgress: setProgress })
      // The summary stays on screen, including on a clean run — closing on
      // success hid the one confirmation that anything had been created.
      setResults(outcome)
      if (outcome.successCount > 0) refetch?.()
    } catch (error) {
      setFormError(
        describeError(error, 'Could not read the CSV file. Please check the format and try again.')
      )
    } finally {
      setIsSubmitting(false)
      setProgress('')
    }
  }

  return (
    <Box sx={{ m: 1 }}>
      <Stack spacing={2}>
        <Box>
          <Button variant="outlined" component="label">
            {csvFile ? csvFile.name : 'Choose CSV File'}
            <input type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} />
          </Button>
        </Box>

        <Button variant="text" onClick={onDownloadSample} sx={{ alignSelf: 'flex-start' }}>
          Download Sample CSV
        </Button>

        <Typography variant="caption" color="text.secondary">
          Every value is read from the CSV — nothing has to be selected here.{' '}
          {allOrNothing
            ? 'The whole file is checked before anything is created: if any row fails, every problem is listed below and nothing is imported.'
            : 'Rows that fail validation are reported below; all valid rows are still imported.'}
          {helpText ? ' ' : ''}
          {helpText}
        </Typography>

        {formError && <Alert severity="error">{formError}</Alert>}

        {ignoredColumns.length > 0 && (
          <Alert severity="info">
            Ignored unknown {ignoredColumns.length > 1 ? 'columns' : 'column'}:{' '}
            {ignoredColumns.join(', ')}
          </Alert>
        )}

        {isSubmitting && progress && (
          <Typography variant="caption" color="text.secondary">
            {progress}
          </Typography>
        )}

        {results && (
          <Alert severity={results.failures.length ? 'warning' : 'success'}>
            <Typography variant="body2">
              {results.successCount === 0 && results.failures.length
                ? `No ${entityPlural} were uploaded.`
                : `${results.successCount} of ${results.total} ${entityPlural} uploaded successfully.`}
            </Typography>
            {results.note && <Typography variant="body2">{results.note}</Typography>}
            {results.failures.length > 0 && (
              <List dense>
                {results.failures.map((failure, index) => (
                  <ListItem key={index} disableGutters>
                    <ListItemText primary={describeFailure(failure)} />
                  </ListItem>
                ))}
              </List>
            )}
          </Alert>
        )}

        {/* Rows that were imported, but not as written. */}
        {results?.warnings?.length > 0 && (
          <Alert severity="info">
            <Typography variant="body2">
              {results.warnings.length} row{results.warnings.length > 1 ? 's' : ''} need a second look:
            </Typography>
            <List dense>
              {results.warnings.map((warning, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemText primary={describeFailure(warning)} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          <Button color="primary" onClick={toggleDrawer} fullWidth size="large" variant="outlined">
            {results ? 'Close' : 'Cancel'}
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            fullWidth
            size="large"
            variant="contained"
          >
            {isSubmitting && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
            Upload {entityPlural}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

BulkUploadPanel.propTypes = {
  entityPlural: PropTypes.string.isRequired,
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  onDownloadSample: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  refetch: PropTypes.func,
  helpText: PropTypes.node,
  // Set by imports whose API validates the whole file before writing anything,
  // so the panel does not promise that valid rows survive a failed upload.
  allOrNothing: PropTypes.bool,
}

export default BulkUploadPanel
